import React, { useState, useEffect, useRef, useCallback } from 'react';
import { imageCache, optimizeImageUrl } from '../../utils/imageCache';
import { ImageSkeleton } from '../Skeleton';

const OptimizedImage = ({ 
  src, 
  alt = '', 
  width,
  height,
  style = {},
  className = '',
  lazy = true,
  placeholder = null,
  onLoad,
  onError,
  quality = 80,
  format = 'webp',
  ...props 
}) => {
  const [imageState, setImageState] = useState({
    loading: true,
    error: false,
    loaded: false,
    objectUrl: null
  });
  
  const imgRef = useRef(null);
  const observerRef = useRef(null);
  const [inView, setInView] = useState(!lazy);

  // 优化图片URL
  const optimizedSrc = optimizeImageUrl(src, { width, height, quality, format });

  // 加载图片
  const loadImage = useCallback(async () => {
    if (!optimizedSrc || imageState.loaded) return;

    setImageState(prev => ({ ...prev, loading: true, error: false }));

    try {
      // 检查缓存
      let blob = imageCache.get(optimizedSrc);
      
      if (!blob) {
        // 从网络加载
        blob = await imageCache.preload(optimizedSrc, { timeout: 15000 });
      }

      // 创建对象URL
      const objectUrl = imageCache.createObjectURL(blob);
      
      setImageState({
        loading: false,
        error: false,
        loaded: true,
        objectUrl
      });

      if (onLoad) onLoad();

    } catch (error) {
      console.warn('图片加载失败:', optimizedSrc, error);
      setImageState({
        loading: false,
        error: true,
        loaded: false,
        objectUrl: null
      });

      if (onError) onError(error);
    }
  }, [optimizedSrc, imageState.loaded, onLoad, onError]);

  // 懒加载观察器
  useEffect(() => {
    if (!lazy || !imgRef.current) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observerRef.current?.disconnect();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '50px' // 提前50px开始加载
      }
    );

    observerRef.current.observe(imgRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [lazy]);

  // 当进入视口时加载图片
  useEffect(() => {
    if (inView && optimizedSrc) {
      loadImage();
    }
  }, [inView, optimizedSrc, loadImage]);

  // 清理对象URL
  useEffect(() => {
    return () => {
      if (imageState.objectUrl) {
        URL.revokeObjectURL(imageState.objectUrl);
      }
    };
  }, [imageState.objectUrl]);

  // 渲染加载状态
  if (imageState.loading && !imageState.loaded) {
    return (
      <div ref={imgRef} style={{ width, height, ...style }} className={className}>
        {placeholder || (
          <ImageSkeleton 
            width={width || '100%'} 
            height={height || 160}
            style={style}
          />
        )}
      </div>
    );
  }

  // 渲染错误状态
  if (imageState.error) {
    return (
      <img
        ref={imgRef}
        src="/images/placeholder.svg"
        alt={alt || "图片加载失败"}
        width={width}
        height={height}
        style={{
          objectFit: 'cover',
          ...style
        }}
        className={className}
        {...props}
      />
    );
  }

  // 渲染图片
  return (
    <img
      ref={imgRef}
      src={imageState.objectUrl || optimizedSrc}
      alt={alt}
      width={width}
      height={height}
      style={{
        display: imageState.loaded ? 'block' : 'none',
        transition: 'opacity 0.3s ease',
        opacity: imageState.loaded ? 1 : 0,
        ...style
      }}
      className={className}
      {...props}
    />
  );
};

export default OptimizedImage;